import { SdkContext } from '../internal'

import type { GalleryImage } from '@/types/gallery'

export type { GalleryImage }

export interface GalleryListResponse {
  items: GalleryImage[]
  pagination: {
    page: number
    totalPages: number
    total: number
    hasNext: boolean
  }
}

export class GalleryResource {
  constructor(private ctx: SdkContext) {}

  async list(params: Record<string, unknown> = {}): Promise<GalleryListResponse> {
    const { spaceId, page = 1, limit = 20, sortBy = 'createdAt', ...rest } = params
    const normalizedSortBy =
      sortBy === 'recent' ? 'createdAt' : sortBy === 'popular' ? 'likes' : sortBy

    return this.ctx.call(
      { httpMethod: 'GET', path: '/gallery/images' },
      { query: { spaceId, page, limit, sortBy: normalizedSortBy, order: 'desc', ...rest } }
    )
  }

  /** F-76: creates images from confirmed `galleryImage` media (up to 50). The images come back private. */
  async createFromMedia(spaceId: string, items: Array<{ mediaId: string; title?: string; description?: string; tags?: string[] }>): Promise<GalleryImage[]> {
    const result = (await this.ctx.call({ httpMethod: 'POST', path: '/gallery/images', hasBody: true }, { body: { spaceId, items } })) as GalleryImage[] | { items?: GalleryImage[] } | null
    return Array.isArray(result) ? result : result?.items || []
  }

  async get(id: string): Promise<GalleryImage> {
    return this.ctx.call({ httpMethod: 'GET', path: `/gallery/images/{id}` }, { id })
  }

  async update(id: string, imageData: Partial<GalleryImage>): Promise<GalleryImage> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: `/gallery/images/{id}`, hasBody: true },
      { id, body: imageData }
    )
  }

  async delete(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/gallery/images/{id}` }, { id })
  }

  async react(id: string, reaction: string): Promise<{ reaction: string }> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/gallery/images/{id}/react`, hasBody: true },
      { id, body: { reaction } }
    )
  }

  async unreact(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/gallery/images/{id}/react` }, { id })
  }
}
